<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-card-title>
              CSV Partner Upload
              <v-spacer></v-spacer>
              <v-btn class="mr-2" color="orange" dark depressed small @click="reportFilter = 'HasOffers'">
                HasOffers
              </v-btn>
              <v-btn class="mr-2" color="purple" dark depressed small @click="reportFilter = 'cellxpert'">
                Cellxpert
              </v-btn>
              <v-btn class="mr-2" color="green" dark depressed small @click="reportFilter = 'custom'">
                Custom
              </v-btn>
              <v-btn class="mr-2" color="blue" dark depressed small @click="reportFilter = 'all'">
                Show all
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-data-table
                :headers="headers"
                :items="filteredPartnerReports"
                :options="options"
                no-data-text="No report configuration found"
                dense
              >
                <template #item.partner="{ value }">
                  <item-logo :name="value.name" :src="value.logoThumbnail" class="mr-2" />{{ value.name }}
                </template>
                <template #item.complete="{ value }">
                  <v-icon v-if="value" color="success">check_circle_outline</v-icon>
                  <v-icon v-else color="error">highlight_off</v-icon>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-icon :disabled="!isPermittedTo('create')" @click="onClickPartner(item)"> backup </v-icon>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-card-title>
              Pending Events - £{{ totalValueInTable }}
              <v-spacer></v-spacer>
              <text-input v-model="search" label="Search" append-icon="search" />
            </v-card-title>
            <v-card-text>
              <v-data-table
                ref="pending"
                :disabled="isLoading"
                :loading="isLoading"
                :headers="pendingHeaders"
                :search="search"
                :items="eventLog"
                dense
              >
                <template #item.date="{ value }">
                  {{ formatDate(value, { separator: '-' }) }}
                </template>
                <template #item.partner="{ value }">
                  <router-link :to="{ name: 'partners.edit', params: { slug: value } }">{{
                    getPartnerNameFromSlug(value)
                  }}</router-link>
                </template>
                <template #item.click_id="{ value }">
                  <valid-click-id :value="value" />
                </template>
                <template #item.amount="{ value }">
                  {{ (value / 100).toFixed(2) }}
                </template>
                <template #item.actions="{ item }">
                  <v-icon :disabled="!isPermittedTo('update')" class="mr-2" small @click="onEditItem(item.uuid)">
                    edit
                  </v-icon>
                  <v-icon :disabled="!isPermittedTo('delete')" small @click="onDeleteItem(item.uuid)">
                    delete
                  </v-icon>
                </template>
              </v-data-table>
              <v-row>
                <v-col class="d-flex justify-end">
                  <v-btn
                    :disabled="!isPermittedTo('create') || !eventLog.length"
                    :loading="isLoading"
                    class="ml-4"
                    color="info"
                    depressed
                    @click="onClickUpload"
                  >
                    <v-icon left>cloud_upload</v-icon>
                    Upload data
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
            <edit-event-modal
              :event="editingEvent"
              :is-disabled="isLoading"
              :is-loading="isLoading"
              :value="showEditModal"
              @modal-confirm="onEditConfirm"
              @modal-cancel="onEditCancel"
            >
              <v-row>
                <v-col sm="4">
                  <autocomplete-input v-model="editingEvent.partner" :items="filteredPartners" label="Partner" />
                </v-col>
                <v-col sm="4">
                  <autocomplete-input v-model="editingEvent.currency" :items="currencies" label="Currency" />
                </v-col>
                <v-col sm="4">
                  <date-picker
                    v-model="editingEvent.date"
                    :error="editingEvent.date == undefined"
                    format="DDMMYYYY"
                    label="Date"
                    hide-details
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col sm="4">
                  <autocomplete-input
                    v-model="editingEvent.event_type"
                    :error="editingEvent.event_type == undefined"
                    :items="eventTypes"
                    class="mb-1"
                    label="Event Type"
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <text-input v-model="editingEvent.click_id" class="mb-1" label="Click ID" hide-details />
                </v-col>
                <v-col sm="4">
                  <autocomplete-input
                    v-model="editingEvent.country"
                    :items="countries"
                    class="mb-1"
                    label="Country"
                    hide-details
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col sm="4">
                  <autocomplete-input
                    v-model="editingEvent.offer_type"
                    :items="offerTypes"
                    label="Offer Type"
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <autocomplete-input
                    v-model="editingEvent.offer"
                    :disabled="!editingEvent.offer_type"
                    :items="filteredOffers.filter(({ type }) => type === editingEvent.offer_type)"
                    class="mb-1"
                    label="Offer"
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <autocomplete-input
                    v-model="editingEvent.website"
                    :items="sites"
                    class="my-1"
                    label="Website"
                    hide-details
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col sm="4">
                  <text-input
                    v-model="editingEvent.raw_amount"
                    :error="editingEvent.raw_amount == undefined"
                    class="my-1"
                    label="Amount"
                    type="number"
                    hide-details
                  />
                </v-col>
              </v-row>
            </edit-event-modal>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <generic-modal
      v-if="showUploadModal"
      v-model="showUploadModal"
      :is-disabled="!file || isLoading"
      :is-loading="isLoading"
      confirm-text="Upload CSV"
      title="Upload partner report"
      persistent
      @modal-confirm="onModalUploadConfirm"
      @modal-cancel="onModalUploadCancel"
    >
      <v-row>
        <v-col sm="6">
          <table class="report-details">
            <tr>
              <th>URL</th>
              <td><a :href="uploadConfiguration.report.partner_portal.url" target="_blank">Portal</a></td>
            </tr>
            <tr>
              <th>Username</th>
              <td>
                {{ uploadConfiguration.report.partner_portal.username }}
                <copy-to-clipboard
                  label="username"
                  :copy-value="uploadConfiguration.report.partner_portal.username"
                  class="ml-2"
                />
              </td>
            </tr>
            <tr>
              <th>Password</th>
              <td>
                {{ uploadConfiguration.report.partner_portal.password }}
                <copy-to-clipboard
                  label="password"
                  :copy-value="uploadConfiguration.report.partner_portal.password"
                  class="ml-2"
                />
              </td>
            </tr>
          </table>

          <div class="mt-2">
            <span class="font-weight-bold">Instructions</span>
            <ol>
              <li v-for="step in uploadConfiguration.report.partner_portal.steps" :key="step">
                {{ step }}
              </li>
            </ol>
          </div>
        </v-col>
        <v-col sm="5" offset-sm="1">
          <div>
            <item-logo
              :name="uploadConfiguration.partner.name"
              :src="uploadConfiguration.partner.logoThumbnail"
              class="mr-2"
            />{{ uploadConfiguration.partner.name }}
          </div>

          <table class="report-details">
            <tr>
              <th>Portal</th>
              <td>{{ uploadConfiguration.report.partner_portal.name }}</td>
            </tr>
            <tr>
              <th>Type</th>
              <td>{{ uploadConfiguration.report.type }}</td>
            </tr>
          </table>
        </v-col>
      </v-row>
      <drag-and-drop-upload v-model="file" accept=".csv, application/vnd.ms-excel" />
      <template #actions>
        <v-btn :disabled="isLoading" :loading="isLoading" color="secondary" outlined @click="onModalUploadChecked">
          Mark as checked
        </v-btn>
      </template>
    </generic-modal>
  </page-loader>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import AutocompleteInput from '@/components/forms/autocomplete-input'
import CopyToClipboard from '@/components/copy-to-clipboard'
import DatePicker from '@/components/forms/date-picker'
import DragAndDropUpload from '@/components/drag-and-drop-upload'
import GenericModal from '@/components/modals/generic-modal'
import ItemLogo from '@/components/item-logo'
import PageLoader from '@/components/page-loader'
import TextInput from '@/components/forms/text-input'
import ValidClickId from '@/components/valid-click-id'

import EditEventModal from '@/views/data/components/edit-event-modal'

import PermissionsHandler from '@/mixins/permissions-handler'

import integrations from '@/services/integrations'
import { offers, partners } from '@/services/offers'

import { formatDate } from '@/utils/date'
import storage from '@/utils/storage'

export default {
  name: 'CSVUpload',
  components: {
    AutocompleteInput,
    CopyToClipboard,
    DatePicker,
    DragAndDropUpload,
    EditEventModal,
    GenericModal,
    ItemLogo,
    PageLoader,
    TextInput,
    ValidClickId,
  },
  mixins: [PermissionsHandler],
  data: () => ({
    isLoading: true,
    configurations: [],
    reportFilter: 'all',
    headers: [
      { text: 'Partner', value: 'partner' },
      { text: 'Portal', value: 'report.partner_portal.name' },
      { text: 'Portal Type', value: 'report.partner_portal.type' },
      { text: 'Report Type', value: 'report.type' },
      { text: 'Checked?', align: 'center', value: 'complete', sortable: false },
      { text: 'Actions', align: 'end', value: 'actions', sortable: false },
    ],
    options: {
      itemsPerPage: -1,
    },
    pendingHeaders: [
      { text: 'Partner', value: 'partner' },
      { text: 'Date', value: 'date', width: 110 },
      { text: 'ID', value: 'click_id', sortable: false },
      { text: 'Event Type', value: 'event_type' },
      { text: 'Country', value: 'country', sortable: false },
      { text: 'Amount (£)', align: 'end', value: 'amount' },
      { text: 'Actions', align: 'end', value: 'actions', sortable: false },
    ],
    eventTypes: [
      { value: 'registration', key: 'Registration' },
      { value: 'ftd', key: 'FTD' },
      { value: 'qftd', key: 'QFTD' },
    ],
    completeReports: [],
    offers: [],
    partners: [],
    showUploadModal: false,
    uploadConfiguration: null,
    file: null,
    search: '',
    eventLog: [],
    totalValueInTable: 0,
    editingEvent: {},
    showEditModal: false,
  }),
  computed: {
    ...mapGetters('options', ['formattedOption', 'getItemsForOption']),
    partnerReports() {
      return this.configurations.reduce((reports, config) => {
        const partner = this.partners.find(({ slug }) => slug === config.partner) || { name: 'Unknown Partner' }
        const rows = config.reports.map(report => {
          const isComplete = this.completeReports.find(completedReport => {
            return (
              partner.slug === completedReport.partner &&
              completedReport.type === report.type &&
              completedReport.portal === report.partner_portal.name
            )
          })
          return {
            complete: !!isComplete,
            partner,
            report,
          }
        })
        return reports.concat(rows)
      }, [])
    },
    filteredPartnerReports() {
      if (this.reportFilter === 'all') return this.partnerReports
      return this.partnerReports.filter(
        ({
          report: {
            partner_portal: { type },
          },
        }) => type === this.reportFilter
      )
    },
    filteredOffers() {
      return this.offers.map(({ name, slug, type }) => ({
        key: name || slug,
        type,
        value: slug,
      }))
    },
    filteredPartners() {
      return this.partners.map(({ name, slug }) => ({
        key: name || slug,
        value: slug,
      }))
    },
    countries() {
      return this.getItemsForOption('countries')
    },
    currencies() {
      return this.getItemsForOption('mainCurrencies')
    },
    offerTypes() {
      return this.getItemsForOption('offerTypes')
    },
    sites() {
      return this.getItemsForOption('sites')
    },
  },
  watch: {
    async eventLog() {
      await this.$nextTick()
      this.updateSumTotal()
    },
    async search() {
      await this.$nextTick()
      this.updateSumTotal()
    },
  },
  async created() {
    this.getCompleteReports()
    await this.getOffersAndPartners()
    await this.getConfigurations()
    await this.getEventLog()
    this.totalValueInTable = this.updateSumTotal()
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async getOffersAndPartners() {
      this.isLoading = true

      try {
        const { data: offerRows } = await offers.getAllOffers({ compact: true })
        this.offers = offerRows
        const { data: partnerRows } = await partners.getAllPartners({ compact: true })
        this.partners = partnerRows
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve Offers & Partners',
        })
      }

      this.isLoading = false
    },
    async getConfigurations() {
      this.isLoading = true

      try {
        const { partners } = await integrations.getConfig('csv')
        this.configurations = partners
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve partner configurations',
        })
      }

      this.isLoading = false
    },
    getCompleteReports() {
      const { reports = [] } = storage.get('csv-report-uploads') || {}
      this.completeReports = reports
    },
    setCompleteReport(partner, report) {
      const { reports = [] } = storage.get('csv-report-uploads') || {}

      const expiry = new Date(new Date().setHours(23, 59, 59))

      storage.set(
        'csv-report-uploads',
        {
          reports: [
            ...reports,
            {
              partner: partner.slug,
              portal: report.partner_portal.name,
              type: report.type,
            },
          ],
        },
        expiry
      )
    },
    onClickPartner(reportData) {
      this.uploadConfiguration = reportData
      this.showUploadModal = true
    },
    async onModalUploadConfirm() {
      this.isLoading = true

      try {
        const { partner, report } = this.uploadConfiguration

        await integrations.uploadCSV(
          { partner: partner.slug, partner_portal: report.partner_portal.name, report_type: report.type },
          this.file
        )

        this.setCompleteReport(partner, report)
        this.getCompleteReports()
        await this.getEventLog()
        this.file = null
        this.showUploadModal = false
        this.$notify({
          type: 'success',
          text: 'CSV uploaded successfully',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Error uploading CSV',
        })
      }

      this.isLoading = false
    },
    onModalUploadCancel() {
      this.uploadConfiguration = null
      this.file = null
      this.showUploadModal = false
    },
    onModalUploadChecked() {
      const { partner, report } = this.uploadConfiguration

      this.setCompleteReport(partner, report)
      this.getCompleteReports()
      this.file = null
      this.showUploadModal = false
    },
    formatDate(str, opts) {
      return formatDate(str, opts)
    },
    getPartnerNameFromSlug(slug) {
      const found = this.filteredPartners.find(partner => partner.value === slug)
      return found ? found.key : slug
    },
    updateSumTotal() {
      const filteredItems =
        this.$refs &&
        this.$refs.pending &&
        this.$refs.pending.$children &&
        this.$refs.pending.$children[0].filteredItems

      const toSum = filteredItems || this.eventLog
      const sum = toSum.reduce((total, { amount }) => total + amount, 0)

      this.totalValueInTable = sum ? (sum / 100).toFixed(2) : 0
    },
    async getEventLog() {
      this.isLoading = true

      try {
        const response = await integrations.getEventLog({
          revenue: true,
          pending: true,
          source: 'csv',
        })
        this.$set(this, 'eventLog', response.events)
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch event logs',
        })
      }

      this.isLoading = false
    },
    onEditItem(uuid) {
      this.editingEvent = this.eventLog.find(event => event.uuid === uuid)
      this.showEditModal = true
    },
    async onEditConfirm() {
      this.isLoading = true

      try {
        await integrations.editEvent(this.editingEvent.uuid, this.editingEvent)
        await this.getEventLog()
        this.showEditModal = false
        this.editingEvent = {}
        this.$notify({
          type: 'success',
          text: 'Event Updated',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save event',
        })
      }

      this.isLoading = false
    },
    onEditCancel() {
      this.showEditModal = false
      this.editingEvent = {}
    },
    async onDeleteItem(uuid) {
      this.isLoading = true
      try {
        await integrations.deleteEvent(uuid)
        await this.getEventLog()

        this.$notify({
          type: 'success',
          text: 'Deleted pending event',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete event',
        })
      }
      this.isLoading = false
    },
    async onClickUpload() {
      this.isLoading = true

      try {
        await integrations.sendToEventCollector({ revenue: true, source: 'csv' })
        await this.getEventLog()
        this.$notify({
          type: 'success',
          text: 'Events uploaded',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to upload events',
        })
      }

      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.report-details {
  width: 100%;

  th {
    text-align: left;
  }
}
</style>
